import { logger } from '@/engine/logging';
/**
 * Управление игровым циклом на основе requestAnimationFrame
 * Предоставляет delta time для плавных анимаций
 */
export class Ticker {
  private animationId: number | null = null;
  private lastTime = 0;
  private isRunning = false;
  private updateCallbacks: ((deltaTime: number) => void)[] = [];

  // FPS tracking
  private frameCount = 0;
  private fpsStartTime = 0;
  private currentFPS = 0;
  private fpsUpdateInterval = 100; // Update FPS every 100ms for more responsive display

  /**
   * Запустить игровой цикл
   */
  public start(): void {
    if (this.isRunning) {
      logger.warn('Ticker уже запущен!', { source: 'game' });
      return;
    }

    this.isRunning = true;
    this.lastTime = performance.now();
    this.fpsStartTime = this.lastTime;
    this.frameCount = 0;
    this.currentFPS = 0;
    this.tick();
  }

  /**
   * Остановить игровой цикл
   */
  public stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Добавить callback для обновления
   */
  public addUpdateCallback(callback: (deltaTime: number) => void): void {
    this.updateCallbacks.push(callback);
  }

  /**
   * Удалить callback для обновления
   */
  public removeUpdateCallback(callback: (deltaTime: number) => void): void {
    const index = this.updateCallbacks.indexOf(callback);
    if (index !== -1) {
      this.updateCallbacks.splice(index, 1);
    }
  }

  /**
   * Основной цикл обновления
   */
  private tick = (): void => {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000; // Конвертируем в секунды
    this.lastTime = currentTime;

    // Update FPS counter
    this.frameCount++;
    const timeSinceFpsStart = currentTime - this.fpsStartTime;

    // Update FPS every 100ms
    if (timeSinceFpsStart >= this.fpsUpdateInterval) {
      this.currentFPS = Math.round((this.frameCount * 1000) / timeSinceFpsStart);
      // logger.info(`FPS updated: ${this.currentFPS} (${this.frameCount} frames in ${timeSinceFpsStart}ms)`, { source: 'ticker' });
      this.frameCount = 0;
      this.fpsStartTime = currentTime;
    }

    // Ограничиваем delta time для предотвращения больших скачков
    const clampedDeltaTime = Math.min(deltaTime, 1 / 30); // Максимум 30 FPS

    // Вызываем все callback'и
    for (const callback of this.updateCallbacks) {
      try {
        callback(clampedDeltaTime);
      } catch (error) {
        logger.error('Ошибка в игровом цикле:', error as Error, { source: 'game' });
      }
    }

    // Планируем следующий кадр
    this.animationId = requestAnimationFrame(this.tick);
  };

  /**
   * Проверить, запущен ли ticker
   */
  public get running(): boolean {
    return this.isRunning;
  }

  /**
   * Получить текущий FPS (обновляется каждую секунду)
   */
  public getFPS(): number {
    if (!this.isRunning) return 0;
    return this.currentFPS;
  }

  /**
   * Получить мгновенный FPS на основе последнего delta time
   */
  public getInstantFPS(): number {
    if (!this.isRunning) return 0;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000;
    return deltaTime > 0 ? Math.round(1 / deltaTime) : 0;
  }
}
